import { yupResolver } from "@hookform/resolvers/yup"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import * as yup from "yup"

import { BottomSheetModal, MessageError } from "@/components/shared"
import { Button, Input, Label } from "@/components/ui"
import useUpdateInfo from "@/modules/helpers/hooks/react-query/use-info"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { User } from "@/modules/types"

const validationSchema = yup.object({
  username: yup
    .string()
    .required("Tên tài khoản là bắt buộc")
    .min(5, "Tên tài khoản phải có ít nhất 5 ký tự"),
  email: yup.string().required("Email là bắt buộc").email("Email không hợp lệ"),
  phoneNumber: yup
    .string()
    .required("Số điện thoại là bắt buộc")
    .matches(/^[0-9]{10}$/, "Số điện thoại phải có 10 chữ số")
    .test("phone-format", "Số điện thoại phải có 10 chữ số", (value) => {
      if (!value) return true
      return /^[0-9]{10}$/.test(value.replace(/\D/g, ""))
    })
})

interface ProfileEditModalProps {
  isVisible: boolean
  setIsVisible: (isVisible: boolean) => void
}

export function ProfileEditModal({ isVisible, setIsVisible }: ProfileEditModalProps) {
  const queryClient = useQueryClient()
  const { mutate: updateProfile } = useUpdateInfo({
    onSuccess: () => {
      toast.success("Cập nhật thông tin thành công!")
      setIsVisible(false)
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
    },
    onError: () => {
      toast.error("Có lỗi xảy ra khi cập nhật thông tin")
    }
  })
  const { user } = useCurrentUser()
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      username: "",
      email: user?.email || "",
      phoneNumber: ""
    }
  })

  useEffect(() => {
    if (isVisible)
      reset({
        username: user?.username || "",
        email: user?.email || "",
        phoneNumber: user?.phoneNumber || ""
      })
  }, [user, isVisible, reset])

  const onSubmit = async (data: Partial<User>) => {
    updateProfile(data)
  }

  return (
    <BottomSheetModal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      title="Chỉnh sửa hồ sơ cá nhân"
    >
      <div className="p-2 ">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Controller
            control={control}
            name="username"
            render={({ field: { onChange, ...field } }) => (
              <MessageError error={errors.username?.message}>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tên tài khoản</Label>
                  <Input
                    {...field}
                    onChange={(e) => {
                      onChange(e.target.value.trim())
                    }}
                    placeholder="Nhập tên tài khoản"
                    className="text-sm"
                  />
                </div>
              </MessageError>
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <MessageError error={errors.email?.message}>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Email</Label>
                  <Input {...field} placeholder="Nhập email" className="text-sm" />
                </div>
              </MessageError>
            )}
          />
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <MessageError error={errors.phoneNumber?.message}>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Số điện thoại (Zalo)</Label>
                  <Input
                    {...field}
                    placeholder="Nhập số điện thoại"
                    inputMode="numeric"
                    className="text-sm"
                  />
                </div>
              </MessageError>
            )}
          />
          <Button
            type="submit"
            className="w-full !h-12 rounded-xl bg-primary hover:bg-gray-800 text-white font-medium py-3"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Đang lưu..." : "Lưu"}
          </Button>
        </form>
      </div>
    </BottomSheetModal>
  )
}
